/**
 * Cascade Timer — "Histórico" drawer (Plan A v2 + Decisión #4 threshold).
 *
 * Mirrors the structure of SrRecentDrawer (inline in LiqMapPage) and
 * CascadeRecentDrawer (predictor IA). When the evaluated-sample pool
 * for a coin is below the backend threshold (TIMER_MIN_FOR_STATS=30),
 * the panel suppresses the rate and shows "Muestra insuficiente (N/30)"
 * — same spirit as the S/R Compartir-X pause during its migration window.
 *
 * Data source: GET /api/liqmap/cascade-timer/recent/list (verifier loop).
 */
import { useEffect, useState } from "react";
import { Loader2, X as XIcon } from "lucide-react";

const COINS = [null, "BTC", "ETH", "SOL", "BNB", "XRP", "ZEC"];

export const CascadeTimerHistoryDrawer = ({ open, onClose, token, apiBase }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [coin, setCoin] = useState(null);

  useEffect(() => {
    if (!open) return undefined;
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const headers = token ? { Authorization: `Bearer ${token}` } : {};
        const q = `days=30${coin ? `&coin=${coin}` : ""}`;
        const res = await fetch(`${apiBase}/liqmap/cascade-timer/recent/list?${q}`, { headers });
        const json = await res.json();
        if (cancelled) return;
        setData(res.ok ? json : { items: [], stats: { total: 0 }, error: json?.detail });
      } catch {
        if (!cancelled) setData({ items: [], stats: { total: 0 }, error: "network" });
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [open, coin, token, apiBase]);

  if (!open) return null;

  const items = data?.items || [];
  const stats = data?.stats || {};
  const cal = data?.calibration || [];
  const enough = !!stats.muestra_suficiente;
  const ver = (stats.aciertos || 0) + (stats.fallos || 0);
  const umbral = stats.umbral_min || 30;
  const tasa = enough ? stats.tasa_real_pct : null;
  const tasaCol = tasa == null ? "#888" : tasa >= 65 ? "#00d084" : tasa >= 45 ? "#f0c800" : "#ff3b5c";

  const fmtDate = (iso) => {
    if (!iso) return "—";
    try {
      return new Date(iso).toLocaleString([], {
        month: "short", day: "2-digit", hour: "2-digit", minute: "2-digit",
      });
    } catch { return iso; }
  };
  const fmtMin = (m) => {
    if (m == null) return "—";
    if (m < 60) return `${Math.round(m)}m`;
    const h = Math.floor(m / 60);
    const r = Math.round(m - h * 60);
    return r > 0 ? `${h}h${r}m` : `${h}h`;
  };
  const fmtPrice = (v, coinHint) => {
    if (v == null) return "—";
    const digits = (coinHint === "XRP" || (coinHint === "SOL" && v < 100)) ? 4 : 2;
    return `$${Number(v).toLocaleString(undefined, { maximumFractionDigits: digits })}`;
  };

  return (
    <div
      className="absolute inset-0 z-40 flex items-end justify-center bg-black/65 px-2 pb-4 sm:items-center sm:pb-0"
      onClick={onClose}
      data-testid="cascade-timer-history-overlay"
    >
      <div
        className="relative w-full max-w-2xl overflow-hidden rounded-2xl border shadow-2xl"
        onClick={(e) => e.stopPropagation()}
        style={{
          background: "rgba(8,5,8,0.97)",
          borderColor: "rgba(249,115,22,0.35)",
          maxHeight: "82vh",
        }}
        data-testid="cascade-timer-history-drawer"
      >
        <div className="flex items-start justify-between border-b border-white/[0.06] px-4 py-3">
          <div>
            <p className="font-mono font-bold" style={{ color: "#f97316", fontSize: 10, letterSpacing: "2px" }}>
              CASCADE TIMER · HISTÓRICO · 30 días
            </p>
            <p className="mt-0.5 font-mono text-[9px] text-white/50">
              Countdown vs realidad — touch-then-bounce (mismo verifier que S/R y Predictor)
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Cerrar"
            data-testid="cascade-timer-history-close"
            className="inline-flex h-7 w-7 items-center justify-center rounded text-white/60 hover:text-white"
          >
            <XIcon size={14} />
          </button>
        </div>

        <div className="overflow-y-auto" style={{ maxHeight: "calc(82vh - 64px)" }}>
          {/* Coin filter */}
          <div className="flex flex-wrap gap-1.5 border-b border-white/5 px-4 py-2.5">
            {COINS.map((c) => {
              const active = coin === c;
              return (
                <button
                  key={c || "all"}
                  type="button"
                  onClick={() => setCoin(c)}
                  data-testid={`cascade-timer-coin-${c || "all"}`}
                  className="rounded-md px-2.5 py-1 font-mono text-[9px] font-bold transition-all"
                  style={active
                    ? { background: "rgba(249,115,22,0.18)", border: "1px solid rgba(249,115,22,0.6)", color: "#f97316" }
                    : { background: "rgba(255,255,255,0.03)", border: "1px solid rgba(255,255,255,0.1)", color: "rgba(255,255,255,0.55)" }
                  }
                >
                  {c || "TODAS"}
                </button>
              );
            })}
          </div>

          {loading && (
            <div className="flex items-center justify-center gap-2 py-10 font-mono text-[11px] text-white/55">
              <Loader2 className="h-4 w-4 animate-spin" /> Cargando…
            </div>
          )}

          {!loading && (
            <>
              {/* Decisión #4 — show insufficient-sample banner instead of any rate */}
              {!enough && (
                <div
                  className="flex flex-wrap items-center justify-between gap-2 border-b border-white/5 px-4 py-3"
                  style={{ background: "rgba(249,115,22,0.05)" }}
                  data-testid="cascade-timer-insufficient-banner"
                >
                  <p className="font-mono text-[10px]" style={{ color: "#f97316" }}>
                    ⏳ Muestra insuficiente ({ver}/{umbral})
                  </p>
                  <p className="font-mono text-[9px] text-white/55">
                    Tasa de acierto y calibración disponibles al alcanzar {umbral} predicciones evaluadas.
                  </p>
                </div>
              )}

              {/* Stats grid — 5 cols (Tasa real shows '—' until threshold) */}
              <div className="grid grid-cols-5 gap-2 border-b border-white/5 px-4 py-3 text-center font-mono" data-testid="cascade-timer-stats">
                <div>
                  <p className="text-[8px] uppercase tracking-wider text-white/45">Total</p>
                  <p className="text-[14px] font-bold text-white">{stats.total || 0}</p>
                </div>
                <div>
                  <p className="text-[8px] uppercase tracking-wider text-white/45">Aciertos</p>
                  <p className="text-[14px] font-bold" style={{ color: "#00d084" }}>{stats.aciertos || 0}</p>
                </div>
                <div>
                  <p className="text-[8px] uppercase tracking-wider text-white/45">Fallos</p>
                  <p className="text-[14px] font-bold" style={{ color: "#ff3b5c" }}>{stats.fallos || 0}</p>
                </div>
                <div title="Predicciones donde el precio nunca llegó a la zona del cluster (±0.3%) dentro de 2× tiempo_estimado.">
                  <p className="text-[8px] uppercase tracking-wider text-white/45">No evaluados</p>
                  <p className="text-[14px] font-bold text-white/55">{stats.no_evaluados || 0}</p>
                </div>
                <div title="Solo expuesta tras 30 evaluadas/moneda.">
                  <p className="text-[8px] uppercase tracking-wider text-white/45">Tasa real</p>
                  <p className="text-[14px] font-bold" style={{ color: tasaCol }} data-testid="cascade-timer-tasa-real">
                    {tasa == null ? "—" : `${tasa}%`}
                  </p>
                </div>
              </div>

              {/* Calibration buckets — counts always visible, rates only after threshold */}
              <div className="border-b border-white/5 px-4 py-3" data-testid="cascade-timer-calibration">
                <p className="mb-2 font-mono text-[8px] uppercase tracking-wider text-white/45">
                  Calibración por probabilidad anunciada
                </p>
                <div className="grid grid-cols-4 gap-1.5">
                  {cal.map((b) => (
                    <div
                      key={b.range}
                      className="rounded-md border border-white/5 px-2 py-1.5 font-mono text-center"
                      data-testid={`cascade-timer-bucket-${b.range}`}
                    >
                      <p className="text-[8px] text-white/50">{b.range}%</p>
                      <p className="text-[11px] font-bold text-white">{b.n || 0}</p>
                      <p className="text-[8px] text-white/40">
                        {b.tasa_pct == null ? "—" : `${b.tasa_pct}% real`}
                      </p>
                    </div>
                  ))}
                </div>
              </div>

              {/* Items list */}
              {items.length === 0 ? (
                <p className="px-4 py-10 text-center font-mono text-[11px] text-white/45">
                  Sin predicciones del Cascade Timer registradas en los últimos {data?.days ?? 30} días{coin ? ` para ${coin}` : ""}.
                </p>
              ) : (
                <ul className="divide-y divide-white/[0.04]">
                  {items.map((it) => {
                    const out = it.outcome;
                    const chip = out === "acierto"
                      ? { bg: "rgba(0,208,132,0.12)", fg: "#00d084", lbl: "✓ Acierto" }
                      : out === "fallo"
                      ? { bg: "rgba(255,59,92,0.12)", fg: "#ff3b5c", lbl: "✗ Fallo" }
                      : out === "no_evaluado"
                      ? { bg: "rgba(255,255,255,0.05)", fg: "#a0a0a0", lbl: "➖ No evaluado" }
                      : { bg: "rgba(255,255,255,0.06)", fg: "#aaa", lbl: "⌛ Pendiente" };
                    const tipoCol = (it.tipo || "").includes("LONG") ? "#ff3b5c" : "#00d084";
                    return (
                      <li
                        key={it.id}
                        className="flex flex-wrap items-center gap-x-3 gap-y-1 px-4 py-2.5 font-mono"
                        data-testid={`cascade-timer-item-${it.id}`}
                      >
                        <span className="text-[11px] font-bold text-white/85">{it.coin}</span>
                        <span className="text-[10px]" style={{ color: tipoCol }}>{it.tipo}</span>
                        <span className="text-[9px] text-white/55">
                          → {fmtPrice(it.cluster_predicho, it.coin)} · prob {it.probabilidad_pct}%
                        </span>
                        <span className="text-[9px] text-white/50">
                          est {fmtMin(it.tiempo_estimado_min)}{it.tiempo_real_min != null ? ` · real ${fmtMin(it.tiempo_real_min)}` : ""}
                        </span>
                        <span className="text-[9px] text-white/40">{fmtDate(it.timestamp_prediccion)}</span>
                        <span
                          className="ml-auto rounded-md px-2 py-0.5 text-[9px] font-bold"
                          style={{ background: chip.bg, color: chip.fg }}
                          data-testid={`cascade-timer-outcome-${out || "pendiente"}`}
                        >
                          {chip.lbl}
                        </span>
                      </li>
                    );
                  })}
                </ul>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default CascadeTimerHistoryDrawer;
